import React, { useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  useTheme,
  CircularProgress,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  useMediaQuery,
  Theme
} from '@mui/material';
import {
  PersonOutlineOutlined as StudentIcon,
  SupervisorAccountOutlined as ParentIcon,
  SchoolOutlined as EducatorIcon
} from '@mui/icons-material';
import { createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { setDoc, doc, serverTimestamp, Timestamp, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { trackPixelEvent, PixelEvents } from '../../utils/meta/metaPixel';
import { UserData, SkillName, categories } from '../UserAndProfile/UserContext';
import { validateEmail } from '../../utils/emailValidation';
import { isFreeModeEnabled } from '../../utils/accessMode';

// Google OAuth provider
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Define the props for the component
interface UserInformationFormProps {
  onComplete: (userData: UserData) => void; // Callback when step is successfully completed
  referralData?: {
    hashedReferralId: string;
    referrerInfo: {
      referrerId: string;
      referrerEmail: string;
      referrerFirstName: string;
      referrerUserRole: 'parent' | 'student' | 'educator';
      linkType: string;
    } | null;
  } | null;
}

// Form data type
type UserInfoFormData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};

type UserRole = 'student' | 'parent' | 'educator' | null;

// Form errors type
interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  role?: string;
  submit?: string;
}

const UserInformationForm: React.FC<UserInformationFormProps> = ({ onComplete, referralData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isFreeMode = isFreeModeEnabled();
  
  // DEBUG: Log referralData when component receives it
  console.log('🔍 UserInformationForm received referralData prop:', referralData);
  
  // Also check sessionStorage as a fallback
  const finalReferralData = referralData || (() => {
    try {
      const stored = sessionStorage.getItem('referralData');
      if (stored) {
        console.log('🔍 UserInformationForm loaded referralData from sessionStorage as fallback:', JSON.parse(stored));
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading referralData from sessionStorage:', error);
    }
    return null;
  })();
  
  const [formData, setFormData] = useState<UserInfoFormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });
  
  const [selectedRole, setSelectedRole] = useState<UserRole>('student');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Honeypot field - invisible to users, catches bots
  const [honeypot, setHoneypot] = useState('');

  // Initialize skills helper function
  const initializeSkills = (): UserData['skills'] => {
    const initialSkills: UserData['skills'] = {};
    Object.values(categories).forEach(skillList => {
      skillList.forEach(skillName => {
        initialSkills[skillName as SkillName] = {
          currentTestQuestionsAttempted: 0,
          currentTestQuestionsCorrect: 0,
          currentTestEasyQuestionsAttempted: 0,
          currentTestEasyQuestionsCorrect: 0,
          easyQuestionsIndex: 0,
          currentTestMediumQuestionsAttempted: 0,
          currentTestMediumQuestionsCorrect: 0,
          mediumQuestionsIndex: 0,
          currentTestHardQuestionsAttempted: 0,
          currentTestHardQuestionsCorrect: 0,
          hardQuestionsIndex: 0,
          overallQuestionsAttempted: 0,
          overallQuestionsCorrect: 0,
        };
      });
    });
    return initialSkills;
  };

  // Create user document helper
  const createUserDocument = async (user: any, firstName: string, lastName: string, role: UserRole) => {
    // Calculate trial end date - educators get unlimited access, others get 14 days
    const trialEndDate = new Date();
    if (role === 'educator') {
      // Set trial end date far in the future for educators (essentially unlimited)
      trialEndDate.setFullYear(trialEndDate.getFullYear() + 10);
    } else {
      // 14 days for students and parents
      trialEndDate.setDate(trialEndDate.getDate() + 14);
    }

    console.log('DEBUG referralData in createUserDocument. At this point, the referring person\'s referralData should have been retrieved previously in the StartFreeTrialRatio.tsx file with the first referral-lookup.ts function call. Then upon finishing CreateUserDocument in 1-UserInfoForm.tsx, it should be added to the referred user\'s Firebase doc referredByUser:', finalReferralData);

    const newUserDocData: UserData = {
      uid: user.uid,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: user.email.toLowerCase().trim(),
      userRole: role!,
      createdAt: serverTimestamp() as any,
      stripeCustomerId: null,
      stripeSubscriptionId: null,
      subscriptionStatus: 'trialing', //should this be 'trialing'? When users pay it should be set to 'active'.
      subscriptionCancelledAt: null,
      profileStep3Completed: false,
      educatorProfileCompleted: false,
      trialEndDate: Timestamp.fromDate(trialEndDate),
      currentPeriodEnd: null,
      cancelAtPeriodEnd: false,
      lastUpdated: null,
      
      // Initialize all UserData fields
      hasTakenSAT: false,
      hasTakenACT: false,
      previousSATScores: { math: '', readingWriting: '' },
      previousACTScores: { math: '', reading: '', writing: '', science: '' },
      hasUpcomingTest: false,
      testDate: '',
      targetScores: { math: '', readingWriting: '' },
      colleges: '',
      intendedMajor: '',
      strengths: '',
      areasToImprove: '',
      motivation: '',
      feedbackStyle: '',
      personalInterests: '',
      grandTotalQuestionsAttempted: 0,
      grandTotalQuestionsCorrect: 0,
      completedSATTests: 0,
      totalDaysPracticed: 0,
      numberOfDaysInAStreak: 0,
      lastPracticedDate: null,
      activeChallenges: [],
      completedChallenges: [],
      skills: initializeSkills(),
      diagnosticTestProgress: null,
      fullTestProgress: null,
      testAttempts: [],
      studyPlan: null,
      organizationName: null,
      jobTitle: null,
      state: null,
      selectedColleges: [],
      referredUsers: [],

      // NEW: Set parentName if student was referred by parent
      parentName: (role === 'student' && 
                   finalReferralData?.referrerInfo?.linkType === 'student-to-parent')
        ? finalReferralData.referrerInfo.referrerFirstName
        : null,

      // Add referredBy data if this user was referred
      referredByUser: finalReferralData?.referrerInfo ? {
        referrerId: finalReferralData.referrerInfo.referrerId,
        referrerEmail: finalReferralData.referrerInfo.referrerEmail,
        referrerFirstName: finalReferralData.referrerInfo.referrerFirstName,
        referrerUserRole: finalReferralData.referrerInfo.referrerUserRole,
        linkType: finalReferralData.referrerInfo.linkType as 'parent-to-student' | 'student-to-parent' | 'student-to-educator' | 'student-to-student' | 'parent-to-parent' | 'educator-to-educator',
        referralDate: new Date().toISOString(),
        hashedReferralId: finalReferralData.hashedReferralId
      } : null,

      // Email preferences with defaults
      preferredEmailDay: 'Friday' as const,
      emailNotificationsEnabled: true,
    };

    await setDoc(doc(db, 'users', user.uid), newUserDocData);
    console.log('New user doc created. Now going to check if referralData exists, and if so we\'ll do updateReferralRecord in 1-UserInfoForm.tsx which calls /api/referral-lookup.ts to update the referring person\'s record to mark the referral as active.');

    // CRITICAL: Update referrer's record to mark referral as active
    if (finalReferralData?.referrerInfo) {
      console.log('referralData?.referrerInfo exists, so we\'ll call updateReferrerRecord in 1-UserInfoForm.tsx which calls /api/referral-lookup.ts to update the referring person\'s record to mark the referral as active.');
      await updateReferrerRecord(finalReferralData, newUserDocData);
      console.log('updateReferrerRecord in 1-UserInfoForm.tsx has been called, and the referring person\'s record should be updated - go check Vercel\'s logs to confirm it has been updated to mark the referral as active.');
      
      // Clear referral data from sessionStorage after successful signup
      sessionStorage.removeItem('referralData');
      console.log('✅ Cleared referral data from sessionStorage after successful signup');
    }

    return newUserDocData;
  };

  // SCALING CONSIDERATION: This runs once per referred user signup
  // Should complete in <2 seconds including API call to referral-lookup.ts.
  const updateReferrerRecord = async (referralData: any, newUser: UserData) => {
    try {
      console.log('Updating referrer record for:', referralData.referrerInfo.referrerId);
      
      const response = await fetch('/api/referral-lookup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'update_referrer',
          referrerId: referralData.referrerInfo.referrerId,
          hashedReferralId: referralData.hashedReferralId,
          newUserData: {
            uid: newUser.uid,
            email: newUser.email,
            firstName: newUser.firstName,
            userRole: newUser.userRole
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ Updated referrer ${newUser.firstName} ${newUser.lastName} (${referralData.referrerInfo.referrerId}) with active referral`);
      } else {
        console.warn('Failed to update referrer record via API:', response.status);
      }
    } catch (error) {
      console.error('Failed to update referrer record:', error);
      // Don't throw - user creation should succeed even if referrer update fails
    }
  };

  // Google OAuth signup
  const handleGoogleSignup = async () => {
    if (!selectedRole) {
      setErrors(prev => ({ ...prev, role: 'Please select your role before continuing.' }));
      return;
    }

    // Honeypot check
    if (honeypot) {
      console.log('Bot detected: honeypot field filled');
      trackPixelEvent(PixelEvents.BLOCKED_FAKE_SIGNUP, {
        content_name: 'Google OAuth Honeypot Triggered',
        content_category: 'Bot Protection',
        reason: 'honeypot_filled'
      });
      setErrors(prev => ({ ...prev, submit: 'Suspicious activity detected. Please try again.' }));
      return;
    }

    setIsGoogleLoading(true);
    setErrors(prev => ({ ...prev, submit: undefined }));

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      // Validate email from Google account
      if (user.email) {
        const emailValidation = validateEmail(user.email);
        if (!emailValidation.isValid) {
          trackPixelEvent(PixelEvents.BLOCKED_FAKE_SIGNUP, {
            content_name: 'Google OAuth Invalid Email',
            content_category: 'Email Validation',
            email_domain: user.email.split('@')[1] || 'unknown',
            reason: 'disposable_or_invalid_tld'
          });
          setErrors(prev => ({ ...prev, submit: emailValidation.error || 'Invalid email address.' }));
          // Sign out the user since we're rejecting the signup
          await auth.signOut();
          return;
        }
      }

      // Check if user already exists
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        // User already exists - sign them in instead of showing error
        console.log('Existing user found, signing them in:', user.email);
        setErrors(prev => ({ ...prev, submit: undefined })); // Clear any errors
        
        // Show success message and redirect to /sat (same as Login.tsx)
        setSuccess('Welcome! Signing you in...');
        
        // Use the same navigation pattern as Login.tsx
        setTimeout(() => {
          window.location.href = '/sat'; // Force navigation to ensure clean state
        }, 1500);
        return;
      }

      // Extract names from Google profile
      const displayName = user.displayName || '';
      const nameParts = displayName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Create user document
      const newUserDocData = await createUserDocument(user, firstName, lastName, selectedRole);

      trackPixelEvent(PixelEvents.INITIATE_CREATE_PROFILE, {
        content_name: 'Google OAuth Signup',
        status: 'success',
        value: 0,
        currency: 'USD',
      });

      onComplete(newUserDocData);

    } catch (error: any) {
      console.error("Error with Google signup:", error);
      let errorMessage = 'Google signup failed. Please try again.';
      
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Signup cancelled. Please try again.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup blocked. Please allow popups and try again.';
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        errorMessage = 'An account already exists with this email using a different sign-in method.';
      }
      
      setErrors(prev => ({ ...prev, submit: errorMessage }));
    } finally {
      setIsGoogleLoading(false);
    }
  };

  // Validation functions
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Honeypot check - if filled, it's a bot
    if (honeypot) {
      console.log('Bot detected: honeypot field filled');
      trackPixelEvent(PixelEvents.BLOCKED_FAKE_SIGNUP, {
        content_name: 'Honeypot Triggered',
        content_category: 'Bot Protection',
        reason: 'honeypot_filled'
      });
      newErrors.submit = 'Suspicious activity detected. Please try again.';
      setErrors(newErrors);
      return false;
    }

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required.';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required.';
    
    // Enhanced email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required.';
    } else {
      const emailValidation = validateEmail(formData.email);
      if (!emailValidation.isValid) {
        trackPixelEvent(PixelEvents.BLOCKED_FAKE_SIGNUP, {
          content_name: 'Invalid Email Blocked',
          content_category: 'Email Validation',
          email_domain: formData.email.split('@')[1] || 'unknown',
          reason: 'disposable_or_invalid_tld'
        });
        newErrors.email = emailValidation.error;
      }
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required.';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long.';
    }
    if (!selectedRole) newErrors.role = 'Please select your role.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleRoleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRole(event.target.value as UserRole);
    if (errors.role) {
      setErrors(prev => ({ ...prev, role: undefined }));
    }
  };

  // Manual form submission
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors(prev => ({ ...prev, submit: undefined }));

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email.toLowerCase().trim(),
        formData.password
      );
      const user = userCredential.user;

      const newUserDocData = await createUserDocument(
        user, 
        formData.firstName, 
        formData.lastName, 
        selectedRole
      );

      trackPixelEvent(PixelEvents.INITIATE_CREATE_PROFILE, {
        content_name: 'Manual Form Signup',
        status: 'success',
        value: 0,
        currency: 'USD',
      });

      onComplete(newUserDocData);
      console.log("Finished onComplete(newUserDocData) in UserInformationForm.tsx with referralData: ", referralData);

    } catch (error: any) {
      console.error("Error creating user:", error);
      let errorMessage = 'Registration failed. Please try again.';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered. Please use a different email or try logging in.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address format.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please use at least 6 characters.';
      }
      setErrors(prev => ({ ...prev, submit: errorMessage }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card elevation={0} sx={{ width: '100%', maxWidth: '600px', margin: 'auto' }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h4" component="h2" gutterBottom textAlign="center" 
          sx={{ mb: { xs: 1, md: 2 } }}>
          Create Your Account
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" 
          sx={{ mb: { xs: 1.5, md: 3 } }}>
          Start your journey with Next Step Education.
        </Typography>

        {/* Role Selection - Show First */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" component="div" sx={{ mb: 1 }}>
            I am a:
          </Typography>
          <RadioGroup
            value={selectedRole || ''}
            onChange={handleRoleChange}
            sx={{'& .MuiFormControlLabel-root': {
                margin: 0, width: '100%', border: '2px solid', borderColor: 'divider', borderRadius: 2,
                mb: 1, transition: 'all 0.2s ease-in-out',
                '&:hover': {borderColor: 'primary.main', backgroundColor: 'action.hover', transform: 'scale(1.02)'},
                '&[aria-checked="true"]': {
                  borderColor: 'primary.main', backgroundColor: 'primary.main', color: 'background.default',
                  '&:hover': {backgroundColor: 'primary.dark', transform: 'scale(1.02)'},
                  '& .MuiTypography-root': {
                    color: 'background.default'
                  },
                },
              },
              '& .MuiFormControlLabel-label': {
                width: '100%', py: 1.5, px: 2,
              }
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              gap: 1,
              '& .MuiFormControlLabel-root': {
                flex: { sm: 1 },
                mb: { xs: 1, sm: 0 }
              }
            }}>
              <FormControlLabel value="student" control={<Radio sx={{ display: 'none' }} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                    <StudentIcon sx={{ mr: 1 }} />
                    <Typography>Student</Typography>
                  </Box>
                }
                aria-checked={selectedRole === 'student'}
              />
              <FormControlLabel value="parent" control={<Radio sx={{ display: 'none' }} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                    <ParentIcon sx={{ mr: 1 }} />
                    <Typography>Parent</Typography>
                  </Box>
                }
                aria-checked={selectedRole === 'parent'}
              />
              <FormControlLabel value="educator" control={<Radio sx={{ display: 'none' }} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                    <EducatorIcon sx={{ mr: 1 }} />
                    <Typography>Educator</Typography>
                  </Box>
                }
                aria-checked={selectedRole === 'educator'}
              />
            </Box>
          </RadioGroup>
          {errors.role && (
            <Typography 
              color="error.main" 
              variant="body2" 
              sx={{ 
                fontWeight: 'bold', 
                fontSize: '0.95rem', 
                color: '#d32f2f',
                mt: 0.5 
              }}
            >
              {errors.role}
            </Typography>
          )}
        </Box>

        {/* Google Sign Up Button */}
        <Button onClick={handleGoogleSignup} disabled={isGoogleLoading || isSubmitting}
          fullWidth variant="outlined"
          sx={{
            mb: 2, py: 1.5, textTransform: 'none', fontSize: '1rem', borderColor: '#dadce0', color: '#3c4043',
            backgroundColor: '#fff',
            '&:hover': {
              backgroundColor: '#f8f9fa', borderColor: '#dadce0',
            },
            '&:disabled': {
              backgroundColor: '#f8f9fa', borderColor: '#dadce0',
            }
          }}
          startIcon={
            isGoogleLoading ? (
              <CircularProgress size={20} />
            ) : (
              <Box
                component="img"
                src="https://developers.google.com/identity/images/g-logo.png"
                alt="Google"
                sx={{ width: 20, height: 20 }}
              />
            )
          }
        >
          {isGoogleLoading ? 'Signing up...' : 'Continue with Google'}
        </Button>

        {/* Divider */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
          <Divider sx={{ flex: 1 }} />
          <Typography variant="body2" sx={{ mx: 2, color: 'text.secondary' }}>
            or
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Box>

        {/* Manual Form */}
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={{ xs: 1.5, md: 2.5 }}>
            <TextField
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              error={!!errors.firstName}
              helperText={errors.firstName}
              required
              fullWidth
              disabled={isSubmitting || isGoogleLoading}
              FormHelperTextProps={{
                sx: { 
                  fontWeight: 'bold', 
                  fontSize: '0.95rem', 
                  color: '#d32f2f !important' 
                }
              }}
            />
            
            <TextField
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              error={!!errors.lastName}
              helperText={errors.lastName}
              required
              fullWidth
              disabled={isSubmitting || isGoogleLoading}
              FormHelperTextProps={{
                sx: { 
                  fontWeight: 'bold', 
                  fontSize: '0.95rem', 
                  color: '#d32f2f !important' 
                }
              }}
            />

            <TextField
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              error={!!errors.email}
              helperText={errors.email}
              required
              fullWidth
              disabled={isSubmitting || isGoogleLoading}
              FormHelperTextProps={{
                sx: { 
                  fontWeight: 'bold', 
                  fontSize: '0.95rem', 
                  color: '#d32f2f !important' 
                }
              }}
            />

            <TextField
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              error={!!errors.password}
              helperText={errors.password}
              required
              fullWidth
              disabled={isSubmitting || isGoogleLoading}
              FormHelperTextProps={{
                sx: { 
                  fontWeight: 'bold', 
                  fontSize: '0.95rem', 
                  color: '#d32f2f !important' 
                }
              }}
            />

            {/* Honeypot field - invisible to humans, catches bots */}
            <TextField
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              sx={{ 
                position: 'absolute',
                left: '-9999px',
                opacity: 0,
                pointerEvents: 'none',
                height: 0,
                width: 0
              }}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            {errors.submit && (
              <Alert 
                severity="error" 
                sx={{ 
                  mt: 2, 
                  fontWeight: 'bold', 
                  fontSize: '0.95rem',
                  '& .MuiAlert-message': {
                    fontWeight: 'bold'
                  }
                }}
              >
                {errors.submit}
              </Alert>
            )}

            {success && (
              <Alert 
                severity="success" 
                sx={{ 
                  mt: 2, 
                  fontWeight: 'bold', 
                  fontSize: '0.95rem',
                  '& .MuiAlert-message': {
                    fontWeight: 'bold'
                  }
                }}
              >
                {success}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={isSubmitting || isGoogleLoading}
              sx={{ mt: 3, py: 1.5, textTransform: 'none', fontSize: '1.25rem' }}
            >
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : selectedRole === 'educator' ? 'Create Free Educator Account' : isFreeMode ? 'Create Free Account' : 'Start 14-Day Free Trial'}
            </Button>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default UserInformationForm;